// Centralized API endpoint helpers & fetch wrappers
// Configure via environment variables:
//   REACT_APP_API_BASE   e.g. http://localhost:5000
//   REACT_APP_API_PREFIX e.g. /api  (set to '' if backend has no /api)

// Default backend host changed from localhost to deployed host
// API base must be supplied via environment (.env) so there's a single source of truth.
const RAW_BASE = process.env.REACT_APP_API_BASE;
if (!RAW_BASE) {
  // Fail fast: developer must set REACT_APP_API_BASE in .env or build env.
  throw new Error('REACT_APP_API_BASE not set. Define it in .env before building.');
}
export const API_BASE = RAW_BASE.replace(/\/$/, '');
export const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/api';
// Set REACT_APP_WITH_CREDENTIALS=true to send cookies (session auth) to API on different origin
export const FETCH_CREDENTIALS = (process.env.REACT_APP_WITH_CREDENTIALS === 'true') ? 'include' : 'same-origin';

// The backend returns file links (e.g. occupant Aadhaar uploads) as host-relative paths
// like "/uploads/...". Used as-is in an <a href>, the browser resolves that against the
// frontend's own origin instead of the backend, so the file never loads. Prefix with
// API_BASE to point it at the backend. Already-absolute URLs are returned unchanged.
export function resolveFileUrl(path) {
  if (!path) return path;
  return /^https?:\/\//i.test(path) ? path : `${API_BASE}${path}`;
}

// URL builders
export const url = {
  occupantsList: (tenant) => `${API_BASE}${API_PREFIX}/tenants/occupants/${encodeURIComponent(tenant)}`,
  occupantDelete: (id) => `${API_BASE}${API_PREFIX}/tenants/occupants/${id}`,
  // (Removed alternates after confirming controller mapping)
  tenantBills: (username) => `${API_BASE}${API_PREFIX}/tenants/${encodeURIComponent(username)}`,
  markBillPaid: (billId) => `${API_BASE}${API_PREFIX}/tenants/markPaid/${billId}`,
  createOrder: (billId) => `${API_BASE}${API_PREFIX}/tenants/createOrder/${billId}`,
  logPaymentFailure: () => `${API_BASE}${API_PREFIX}/tenants/logFailure`,
  complaintsList: (tenant) => `${API_BASE}${API_PREFIX}/tenants/complaints/${encodeURIComponent(tenant)}`,
  complaintsWithdraw: (id) => `${API_BASE}${API_PREFIX}/tenants/complaints/${id}/withdraw`,
  complaintsAdd: () => `${API_BASE}${API_PREFIX}/tenants/complaints`,
  adminUsersAll: () => `${API_BASE}${API_PREFIX}/users/all`,
  adminUserAdd: () => `${API_BASE}${API_PREFIX}/users/add`,
  adminUserUpdate: (id) => `${API_BASE}${API_PREFIX}/users/update/${id}`,
  adminUserDelete: (id) => `${API_BASE}${API_PREFIX}/users/delete/${id}`,
  // Admin occupants & verify
  adminOccupantsAll: () => `${API_BASE}${API_PREFIX}/admin/occupants`,
  occupantVerify: (id) => `${API_BASE}${API_PREFIX}/tenants/occupants/verify/${id}`,
  assistantAsk: () => `${API_BASE}${API_PREFIX}/assistant/ask`,
  login: () => `${API_BASE}${API_PREFIX}/users/login`,
  registrationStart: () => `${API_BASE}${API_PREFIX}/users/registration/start`,
  registrationFinish: () => `${API_BASE}${API_PREFIX}/users/registration/finish`,
  vacatePreview: () => `${API_BASE}${API_PREFIX}/tenants/vacate/preview`,
  vacateStatus: () => `${API_BASE}${API_PREFIX}/tenants/vacate/status`,
  vacateRequest: () => `${API_BASE}${API_PREFIX}/tenants/vacate/request`,
  vacateCancel: () => `${API_BASE}${API_PREFIX}/tenants/vacate/cancel`,
  adminVacateAll: () => `${API_BASE}${API_PREFIX}/admin/vacate/all`,
  adminVacateApprove: (id) => `${API_BASE}${API_PREFIX}/admin/vacate/${id}/approve`,
  adminVacateSettle: (id) => `${API_BASE}${API_PREFIX}/admin/vacate/${id}/settle`,
  adminVacateSettled: () => `${API_BASE}${API_PREFIX}/admin/vacate/settled`,
  adminVacateFinalize: (id) => `${API_BASE}${API_PREFIX}/admin/vacate/${id}/finalize`,
  adminNotes: (month) => `${API_BASE}${API_PREFIX}/admin/notes?month=${encodeURIComponent(month)}`,
  adminExpenses: (month) => `${API_BASE}${API_PREFIX}/admin/expenses${month ? `?month=${encodeURIComponent(month)}` : ''}`,
  adminExpense: (id) => `${API_BASE}${API_PREFIX}/admin/expenses/${encodeURIComponent(id)}`,
  adminNoteSave: (room, month) => `${API_BASE}${API_PREFIX}/admin/notes/${encodeURIComponent(room)}/${encodeURIComponent(month)}`,
  adminArchivedTenants: () => `${API_BASE}${API_PREFIX}/admin/archived-tenants`,
  vacateAcknowledge: () => `${API_BASE}${API_PREFIX}/tenants/vacate/acknowledge`
};

// Extended user move-in & deposit specific helpers
export const userMoveInDepositUrl = {
  self: (username) => `${API_BASE}${API_PREFIX}/users/me/movein-deposit?username=${encodeURIComponent(username)}`,
  update: (id) => `${API_BASE}${API_PREFIX}/users/${id}/movein-deposit`,
  createOrder: () => `${API_BASE}${API_PREFIX}/users/deposit/createOrder`,
  verify: () => `${API_BASE}${API_PREFIX}/users/deposit/verify`
};

// The backend requires a Bearer token (issued at login) on every protected route.
// Login.js stores the whole login response -- including the token -- under
// localStorage['user'], so we just read it back out here.
export function getToken() {
  try {
    const stored = localStorage.getItem('user');
    return stored ? (JSON.parse(stored).token || null) : null;
  } catch (_) {
    return null;
  }
}

export function authHeaders(extra = {}) {
  const token = getToken();
  return token ? { ...extra, Authorization: `Bearer ${token}` } : extra;
}

// Drop-in replacement for fetch() that attaches the Authorization header.
// A 401 here always means "this browser's login is no longer valid" (no token,
// expired, or -- since the account is limited to one signed-in device -- superseded
// by a login elsewhere). Rather than let every screen fail silently with its own
// generic error, force this browser back to Login with the server's own reason.
export async function authFetch(input, init = {}) {
  const hadToken = !!getToken();
  const res = await fetch(input, { credentials: FETCH_CREDENTIALS, ...init, headers: authHeaders(init.headers || {}) });
  if (res.status === 401 && hadToken) {
    let message = 'Your session has ended. Please log in again.';
    try {
      const data = await res.clone().json();
      if (data && data.error) message = data.error;
    } catch { /* body wasn't JSON; keep the default message */ }
    localStorage.removeItem('user');
    sessionStorage.setItem('loginNotice', message);
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }
  return res;
}

// Only these may ever be shown, identified by the file's own leading bytes rather
// than the name or Content-Type a client or server claims.
const VIEWABLE_SIGNATURES = [
  { type: 'image/png', bytes: [0x89, 0x50, 0x4e, 0x47] },
  { type: 'image/jpeg', bytes: [0xff, 0xd8, 0xff] },
  { type: 'application/pdf', bytes: [0x25, 0x50, 0x44, 0x46] }, // "%PDF"
];

// Fetches a protected backend file (e.g. an occupant's Aadhaar upload) for the in-page
// FileViewer. A plain <a href> can't carry the Authorization header /uploads/* requires,
// so this fetches with the header and returns a blob URL -- which lives on this app's
// origin, so anything that isn't a real PNG/JPEG/PDF (an HTML or SVG file renamed .png
// could run script as the logged-in user) is refused, and the blob is re-typed from its
// actual contents. The caller must URL.revokeObjectURL(url) when done.
export async function fetchAuthenticatedFile(path) {
  const res = await authFetch(resolveFileUrl(path));
  if (res.status === 404) throw new Error('This file is no longer available.');
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const head = new Uint8Array(await blob.slice(0, 8).arrayBuffer());
  const match = VIEWABLE_SIGNATURES.find(s => s.bytes.every((b, i) => head[i] === b));
  if (!match) throw new Error('This file type cannot be displayed.');
  return { url: URL.createObjectURL(new Blob([blob], { type: match.type })), type: match.type };
}
